import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef, HostListener, Input, NgZone, OnDestroy, Renderer2, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { Subscription, take } from 'rxjs';
import { ComponentBaseComponent } from '../../component-base/component-base.component';
import { ContextMenuRequest, ContextMenuResolver, ContextMenuScope, ContextMenuService } from '../context-menu.service';
import { ContextMenuComponent } from '../context-menu/context-menu.component';

@Component({
    selector: 'tt-context-menu-container',
    standalone: false,
    templateUrl: './context-menu-container.component.html',
    styleUrls: ['./context-menu-container.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [ContextMenuScope],
})
export class ContextMenuContainerComponent<T = unknown> extends ComponentBaseComponent implements OnDestroy {
    @Input() ttContext?: T;
    @Input() ttContextResolver?: ContextMenuResolver<T>;
    @Input() set ttDisabled(value: BooleanInput) { this.disabled = coerceBooleanProperty(value); }
    @ViewChild('popup', { static: true }) private popup!: TemplateRef<unknown>;
    @ContentChild(ContextMenuComponent, { descendants: false }) private menu?: ContextMenuComponent;

    public context?: T;
    public open = false;
    private disabled = false;
    private destroyed = false;
    private restoreFocus = true;
    private focusTarget?: HTMLElement;
    private activeTarget?: Element;
    private listeners = new Subscription();
    private overlayRef?: OverlayRef;
    private position?: FlexibleConnectedPositionStrategy;

    constructor(private overlay: Overlay, private scope: ContextMenuScope, private viewContainer: ViewContainerRef,
        private menus: ContextMenuService, private zone: NgZone, private changeDetector: ChangeDetectorRef,
        private element: ElementRef<HTMLElement>, private renderer: Renderer2) {
        super();
    }

    @HostListener('contextmenu', ['$event'])
    onContextMenu(event: MouseEvent): void {
        this.show(event, event.button === 2 ? 'mouse' : 'keyboard');
    }

    @HostListener('keydown', ['$event'])
    onKeydown(event: KeyboardEvent): void {
        if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey)) {
            this.show(event, 'keyboard');
        }
    }

    public close(restoreFocus = true): void {
        this.restoreFocus = restoreFocus;
        this.menu?.close();
        this.finishClose();
    }

    private show(event: MouseEvent | KeyboardEvent, source: ContextMenuRequest['trigger']): void {
        const target = event.target instanceof Element ? event.target : undefined;
        if (this.destroyed || this.disabled || !this.menu || !target || event.defaultPrevented
            || target.closest('input, textarea, select')
            || (target instanceof HTMLElement && target.isContentEditable)
            || target.closest<HTMLElement>('[contenteditable]')?.isContentEditable) return;
        const request: ContextMenuRequest = { target, event, trigger: source };
        const context = this.ttContextResolver ? this.ttContextResolver(request) : this.ttContext;
        if (this.ttContextResolver && context === null) return;

        this.menus.activate(this, () => this.close(false));
        this.context = context ?? undefined;
        this.scope.action = { ...request, context };
        const active = document.activeElement;
        this.focusTarget = source === 'keyboard' && target instanceof HTMLElement ? target
            : target.closest<HTMLElement>('button, a[href], [tabindex]') ?? (active instanceof HTMLElement ? active : undefined);
        this.restoreFocus = true;
        this.open = true;
        this.activeTarget = this.findContextTarget(target);
        this.renderer.addClass(this.activeTarget, 'tt-context-menu-target--active');
        this.changeDetector.markForCheck();
        event.preventDefault();
        event.stopPropagation();

        const rect = target.getBoundingClientRect();
        const point = source === 'mouse' && event instanceof MouseEvent
            ? { x: event.clientX, y: event.clientY } : { x: rect.left, y: rect.bottom };
        if (!this.overlayRef) {
            this.position = this.overlay.position().flexibleConnectedTo(point)
                .withViewportMargin(8).withPush(true).withGrowAfterOpen(true)
                .withPositions([
                    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
                    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
                    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
                    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
                ]);
            this.overlayRef = this.overlay.create({ positionStrategy: this.position });
        } else {
            this.position?.setOrigin(point);
        }
        this.menu.prepareOpen();
        this.listeners.add(this.menu.closed.subscribe(() => this.finishClose()));
        this.overlayRef.attach(new TemplatePortal(this.popup, this.viewContainer));
        // Scope the stacking level to this popup; legacy body-attached menus also
        // use this level to appear above the application's dialogs.
        this.overlayRef.hostElement.style.zIndex = '1000000';
        // Translated labels and conditional items can change the popup's size after opening.
        const sizeObserver = new ResizeObserver(() => {
            if (this.open) this.overlayRef?.updatePosition();
        });
        this.zone.runOutsideAngular(() => sizeObserver.observe(this.overlayRef!.overlayElement));
        this.listeners.add(() => sizeObserver.disconnect());

        // Conditional projected items belong to the consumer's view. Wait for that
        // view to update before choosing the first focusable item.
        this.listeners.add(this.zone.onStable.pipe(take(1)).subscribe(() => {
            if (this.open) {
                this.overlayRef?.updatePosition();
                this.menu?.focusFirstItem(source);
            }
        }));
        const outside = (outsideEvent: Event) => {
            if (outsideEvent.target instanceof Node && !this.menu?.contains(outsideEvent.target)) this.close(false);
        };
        const scroll = (scrollEvent: Event) => {
            if (scrollEvent.target instanceof Node && this.menu?.contains(scrollEvent.target)) return;
            this.close();
        };
        const resize = () => this.close();
        document.addEventListener('pointerdown', outside, true);
        document.addEventListener('click', outside, true);
        document.addEventListener('contextmenu', outside, true);
        document.addEventListener('scroll', scroll, true);
        window.addEventListener('resize', resize);
        this.listeners.add(() => {
            document.removeEventListener('pointerdown', outside, true);
            document.removeEventListener('click', outside, true);
            document.removeEventListener('contextmenu', outside, true);
            document.removeEventListener('scroll', scroll, true);
            window.removeEventListener('resize', resize);
        });
    }

    private findContextTarget(target: Element): Element {
        const explicitTarget = target.closest('[ttContextMenuTarget]');
        if (explicitTarget && this.element.nativeElement.contains(explicitTarget)) return explicitTarget;
        const control = target.closest('button, a[href], [role="row"], [role="treeitem"], [tabindex]');
        return control && this.element.nativeElement.contains(control) ? control : target;
    }

    private finishClose(): void {
        if (!this.open) return;
        this.open = false;
        if (this.activeTarget) this.renderer.removeClass(this.activeTarget, 'tt-context-menu-target--active');
        this.activeTarget = undefined;
        this.listeners.unsubscribe();
        this.listeners = new Subscription();
        this.menus.release(this);
        this.scope.action = undefined;
        this.context = undefined;
        const active = document.activeElement;
        // Do not take focus back from a dialog or another control opened by an action.
        if (!this.destroyed && this.restoreFocus && this.focusTarget?.isConnected
            && (active === document.body || (active instanceof Node && this.menu?.contains(active)))) {
            this.focusTarget.focus({ preventScroll: true });
        }
        this.focusTarget = undefined;
        this.overlayRef?.detach();
        this.changeDetector.markForCheck();
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.close(false);
        this.listeners.unsubscribe();
        this.overlayRef?.dispose();
    }
}
